import { useEffect, useRef } from 'react';
import { requestSubscription, useRelayEnvironment } from 'react-relay';

import { getLogger } from '@confluence/logger';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { HomeRecentAgentSessionsSubscription as Subscription } from './__generated__/HomeRecentAgentSessionsSubscription.graphql.ts';
import HomeRecentAgentSessionsSubscription from './__generated__/HomeRecentAgentSessionsSubscription.graphql.ts';

const logger = getLogger('home-recent-agent-sessions-subscription');

export function useHomeRecentAgentSessionsSubscription(
	agentSessionsQueryResult: { refetch: () => Promise<unknown> },
	rovoActivationId?: string | null,
): void {
	const { cloudId, userId } = useSessionData();
	const environment = useRelayEnvironment();
	const agentSessionsQueryRefetchRef = useRef(agentSessionsQueryResult.refetch);
	agentSessionsQueryRefetchRef.current = agentSessionsQueryResult.refetch;

	useEffect(() => {
		if (!userId || !cloudId || !rovoActivationId) return;

		let refreshTimer: ReturnType<typeof setTimeout> | undefined;
		const subscription = requestSubscription<Subscription>(environment, {
			subscription: HomeRecentAgentSessionsSubscription,
			variables: {
				accountId: userId,
				cloudId,
				workspaceAri: `ari:cloud:rovo::workspace/${rovoActivationId}`,
			},
			onNext: (response) => {
				if (!response?.convoai_onInboxSessionChanged) return;
				clearTimeout(refreshTimer);
				refreshTimer = setTimeout(() => {
					refreshTimer = undefined;
					void agentSessionsQueryRefetchRef
						.current()
						.catch((error) => logger.error`Home agent sessions refetch failed: ${error}`);
				}, 250);
			},
			onError: (error) => logger.error`Home agent session subscription failed: ${error}`,
		});

		return () => {
			clearTimeout(refreshTimer);
			subscription.dispose();
		};
	}, [cloudId, environment, rovoActivationId, userId]);
}
